import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS

// INTEFACES
import { IAppIntegrationShippingPreset } from '../../../app-integration-account/intefaces/app-integration.interface';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { AppIntegrationPresetApiService } from '../../../shared/services/api/app-integration/app-integration-preset-api.service';

// UTILS
import { trackById } from '../../../shared/util/track-by.util';
import { UiConfirmationDialogComponent } from '../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

interface DataInterface {
  appIntegrationAccountId :number | null;
  shippingPresets :IAppIntegrationShippingPreset[];
}

@Component({
  selector: 'lum-sales-order-manage-presets',
  templateUrl: './sales-order-manage-presets.component.html',
  styleUrls: ['./sales-order-manage-presets.component.scss'],
})
export class SalesOrderManagePresetsComponent implements OnInit, OnDestroy {
  isVisiblePresetForm :boolean = false;
  selectedPreset :IAppIntegrationShippingPreset | null = null;
  shippingPresets :IAppIntegrationShippingPreset[] = [];

  private onDestroy$ = new Subject();
  private loading$ = new BehaviorSubject<boolean>(false);

  trackById = trackById;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :DataInterface,
    private appIntegrationPresetApiService :AppIntegrationPresetApiService,
    private snackBar :MatSnackBar,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    const { appIntegrationAccountId } = this.data;
    if (!appIntegrationAccountId) {
      this.snackBar.open('No app integration account id provided', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.shippingPresets = this.data.shippingPresets;
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onCancel(shippingPreset :IAppIntegrationShippingPreset | null) :void {
    this.isVisiblePresetForm = false;
    if (shippingPreset) {
      // Check if preset already exists in list then update else add
      const presetIndex = this.data.shippingPresets.findIndex(
        (preset) => preset.id === shippingPreset.id,
      );
      if (presetIndex !== -1) {
        this.data.shippingPresets[presetIndex] = shippingPreset;
      } else {
        this.data.shippingPresets.push(shippingPreset);
      }
    }

    this.selectedPreset = null;
  }

  onDelete(id :number) :void {
    this.loading$.next(true);
    this.appIntegrationPresetApiService
      .delete(id)
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: () => {
          (this.shippingPresets = this.shippingPresets.filter(
            (preset) => preset.id !== id,
          )),
            this.snackBar.open('Shipping preset deleted', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
        },
        error: (error) => {
          this.snackBar.open('Error deleting shipping preset', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  confirmDeleteAction(id :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message:
            'Are you sure you want to delete this preset? This action cannot be undone.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.onDelete(id);
        }
      });
  }

  onSetDefault(id :number) :void {
    this.loading$.next(true);
    this.appIntegrationPresetApiService
      .setDefault(id, {
        appIntegrationAccountId: this.data.appIntegrationAccountId
          ? this.data.appIntegrationAccountId.toString()
          : '',
      })
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (preset :IAppIntegrationShippingPreset) => {
          // Update preset in list and remove isDefault true from other presets
          this.shippingPresets = this.shippingPresets.map((preset) => ({
            ...preset,
            isDefault: false,
          }));

          const presetIndex = this.shippingPresets.findIndex(
            (preset) => preset.id === id,
          );

          if (presetIndex !== -1) {
            this.shippingPresets[presetIndex] = {
              ...preset,
              isDefault: true,
            };
          }

          this.snackBar.open('Shipping preset set as default', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open('Error setting shipping preset as default', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onUpdate(shippingPreset :IAppIntegrationShippingPreset) :void {
    this.selectedPreset = shippingPreset;
    this.isVisiblePresetForm = true;
  }
}
